import React, { useMemo, useState, Component } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { CreditCard, MessageSquare, Bot, CheckCircle2, TrendingUp, Download, AlertTriangle, Activity, SearchCheck, BarChart3, Lock, FileText, Rocket, Palette, Blocks, Layers, GitBranch, Globe, Filter, Calendar, ChevronDown, X } from 'lucide-react';
// ─── Types ────────────────────────────────────────────────────────────────────
type ViewDimension = 'global' | 'agent' | 'service' | 'chat';
type TimeRange = '7d' | '14d' | '30d' | '90d';
interface AgentDef {
  id: string;
  name: string;
  shortName: string;
  icon: React.ElementType;
  color: string;
  bg: string;
  chartColor: string;
  price: number;
  msgs: number;
  hired: boolean;
}
interface ServiceDef {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  chartColor: string;
  msgs: number;
}
interface ChatSession {
  id: string;
  title: string;
  msgs: number;
  date: string;
  agent: string;
}
// ─── Mock Data ────────────────────────────────────────────────────────────────
const agents: AgentDef[] = [{
  id: 'scrum',
  name: 'Scrum Master Pro',
  shortName: 'Scrum',
  icon: Activity,
  color: 'text-emerald-400',
  bg: 'bg-emerald-500',
  chartColor: '#34d399',
  price: 0.02,
  msgs: 145,
  hired: true
}, {
  id: 'qa',
  name: 'QA Inspector',
  shortName: 'QA',
  icon: SearchCheck,
  color: 'text-rose-400',
  bg: 'bg-rose-500',
  chartColor: '#fb7185',
  price: 0.03,
  msgs: 12,
  hired: false
}, {
  id: 'analytics',
  name: 'Data Analyst',
  shortName: 'Analytics',
  icon: BarChart3,
  color: 'text-cyan-400',
  bg: 'bg-cyan-500',
  chartColor: '#22d3ee',
  price: 0.02,
  msgs: 89,
  hired: true
}, {
  id: 'security',
  name: 'Security Guardian',
  shortName: 'Security',
  icon: Lock,
  color: 'text-emerald-400',
  bg: 'bg-emerald-500',
  chartColor: '#4ade80',
  price: 0.05,
  msgs: 0,
  hired: false
}, {
  id: 'docs',
  name: 'Doc Writer',
  shortName: 'Docs',
  icon: FileText,
  color: 'text-amber-400',
  bg: 'bg-amber-500',
  chartColor: '#fbbf24',
  price: 0.01,
  msgs: 153,
  hired: true
}, {
  id: 'devops',
  name: 'DevOps Engineer',
  shortName: 'DevOps',
  icon: Rocket,
  color: 'text-orange-400',
  bg: 'bg-orange-500',
  chartColor: '#fb923c',
  price: 0.04,
  msgs: 0,
  hired: false
}, {
  id: 'ux',
  name: 'UX Reviewer',
  shortName: 'UX',
  icon: Palette,
  color: 'text-violet-400',
  bg: 'bg-violet-500',
  chartColor: '#a78bfa',
  price: 0.03,
  msgs: 0,
  hired: false
}, {
  id: 'architect',
  name: 'System Architect',
  shortName: 'Architect',
  icon: Blocks,
  color: 'text-sky-400',
  bg: 'bg-sky-500',
  chartColor: '#38bdf8',
  price: 0.06,
  msgs: 0,
  hired: false
}];
const services: ServiceDef[] = [{
  id: 'jira',
  name: 'Jira',
  icon: Activity,
  color: 'text-blue-400',
  chartColor: '#60a5fa',
  msgs: 198
}, {
  id: 'confluence',
  name: 'Confluence',
  icon: FileText,
  color: 'text-sky-400',
  chartColor: '#38bdf8',
  msgs: 87
}, {
  id: 'slack',
  name: 'Slack',
  icon: MessageSquare,
  color: 'text-purple-400',
  chartColor: '#c084fc',
  msgs: 45
}, {
  id: 'github',
  name: 'GitHub',
  icon: GitBranch,
  color: 'text-slate-300',
  chartColor: '#94a3b8',
  msgs: 57
}];
const chatSessions: ChatSession[] = [{
  id: 'chat-1',
  title: 'Sprint planning PIAE-Sprint 12',
  msgs: 34,
  date: 'Hoy',
  agent: 'scrum'
}, {
  id: 'chat-2',
  title: 'Análisis de velocity Q3',
  msgs: 28,
  date: 'Ayer',
  agent: 'analytics'
}, {
  id: 'chat-3',
  title: 'Documentar API de pagos',
  msgs: 45,
  date: 'Hace 2 días',
  agent: 'docs'
}, {
  id: 'chat-4',
  title: 'Review bugs críticos',
  msgs: 19,
  date: 'Hace 3 días',
  agent: 'scrum'
}, {
  id: 'chat-5',
  title: 'Migración base de datos',
  msgs: 22,
  date: 'Hace 4 días',
  agent: 'docs'
}, {
  id: 'chat-6',
  title: 'Backlog grooming',
  msgs: 41,
  date: 'Hace 5 días',
  agent: 'scrum'
}];
const invoices = [{
  id: 'INV-2026-10',
  period: 'Octubre 2026',
  msgs: 387,
  agents: 3,
  amount: 0.0,
  status: 'Pagado' as const
}, {
  id: 'INV-2026-09',
  period: 'Septiembre 2026',
  msgs: 542,
  agents: 3,
  amount: 1.24,
  status: 'Pagado' as const
}, {
  id: 'INV-2026-08',
  period: 'Agosto 2026',
  msgs: 410,
  agents: 2,
  amount: 0.0,
  status: 'Pagado' as const
}];
// Generate chart data based on filters
function generateChartData(days: number, dimension: ViewDimension, filterId?: string): any[] {
  const data: any[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const label = `${d.getDate()}/${d.getMonth() + 1}`;
    if (dimension === 'global') {
      data.push({
        date: label,
        mensajes: Math.floor(Math.random() * 40) + 5,
        errores: Math.floor(Math.random() * 4),
        jira: Math.floor(Math.random() * 20) + 2,
        confluence: Math.floor(Math.random() * 12),
        slack: Math.floor(Math.random() * 6),
        github: Math.floor(Math.random() * 8)
      });
    } else if (dimension === 'agent') {
      const agent = agents.find(a => a.id === filterId);
      const base = agent?.hired ? Math.floor(Math.random() * 25) + 3 : Math.floor(Math.random() * 3);
      data.push({
        date: label,
        mensajes: base,
        errores: Math.floor(Math.random() * 2),
        tools: Math.floor(base * 0.7)
      });
    } else if (dimension === 'service') {
      const svc = services.find(s => s.id === filterId);
      const base = svc ? Math.floor(Math.random() * 15) + 2 : 5;
      data.push({
        date: label,
        lecturas: Math.floor(base * 0.6),
        escrituras: Math.floor(base * 0.3),
        errores: Math.floor(Math.random() * 2)
      });
    } else if (dimension === 'chat') {
      const session = chatSessions.find(c => c.id === filterId);
      const base = session ? Math.floor(Math.random() * 8) + 1 : 3;
      data.push({
        date: label,
        mensajes: i < 5 ? base : 0,
        tokens: i < 5 ? base * 120 : 0
      });
    }
  }
  return data;
}
// ─── Dropdown Component ───────────────────────────────────────────────────────
function Dropdown({
  label,
  value,
  options,
  onChange,
  icon: Icon
}: {
  label: string;
  value: string;
  options: {
    id: string;
    label: string;
    icon?: React.ElementType;
    color?: string;
    sublabel?: string;
  }[];
  onChange: (id: string) => void;
  icon?: React.ElementType;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.id === value);
  return <div className="relative" data-id="element-3499">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-800 text-sm text-slate-200 hover:border-slate-600 transition-colors" data-id="element-3500">
        {Icon && <Icon size={14} className="text-slate-400" data-id="element-3501" />}
        <span className="max-w-[120px] truncate" data-id="element-3502">
          {selected?.label || label}
        </span>
        <ChevronDown size={12} className={`text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-3503" />
      </button>

      {open && <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-id="element-3504" />
          <div className="absolute top-full mt-1 left-0 z-50 w-64 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150" data-id="element-3505">
            <div className="max-h-64 overflow-y-auto p-1" data-id="element-3506">
              {options.map(opt => {
            const OptIcon = opt.icon;
            return <button key={opt.id} onClick={() => {
              onChange(opt.id);
              setOpen(false);
            }} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${value === opt.id ? 'bg-indigo-900/30 text-indigo-300' : 'text-slate-300 hover:bg-slate-800'}`} data-id="element-3507">
                    {OptIcon && <OptIcon size={14} className={opt.color || 'text-slate-400'} data-id="element-3508" />}
                    <div className="flex-1 min-w-0" data-id="element-3509">
                      <p className="text-sm font-medium truncate" data-id="element-3510">
                        {opt.label}
                      </p>
                      {opt.sublabel && <p className="text-[10px] text-slate-500 truncate" data-id="element-3511">
                          {opt.sublabel}
                        </p>}
                    </div>
                    {value === opt.id && <CheckCircle2 size={14} className="text-indigo-400 shrink-0" data-id="element-3512" />}
                  </button>;
          })}
            </div>
          </div>
        </>}
    </div>;
}
// ─── Main Component ───────────────────────────────────────────────────────────
export function BillingPanel() {
  const [limitAlert, setLimitAlert] = useState(true);
  const [limitAmount, setLimitAmount] = useState('50');
  // Chart filters
  const [viewDimension, setViewDimension] = useState<ViewDimension>('global');
  const [selectedAgent, setSelectedAgent] = useState(agents[0].id);
  const [selectedService, setSelectedService] = useState(services[0].id);
  const [selectedChat, setSelectedChat] = useState(chatSessions[0].id);
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const freeTierLimit = 500;
  const usedMessages = 387;
  const isExhausted = usedMessages >= freeTierLimit;
  const progressPercent = Math.min(usedMessages / freeTierLimit * 100, 100);
  const totalCost = agents.reduce((acc, a) => acc + a.msgs * a.price, 0);
  const days = timeRange === '7d' ? 7 : timeRange === '14d' ? 14 : timeRange === '30d' ? 30 : 90;
  const filterId = viewDimension === 'agent' ? selectedAgent : viewDimension === 'service' ? selectedService : viewDimension === 'chat' ? selectedChat : undefined;
  const chartData = useMemo(() => generateChartData(days, viewDimension, filterId), [days, viewDimension, filterId]);
  // Dimension options
  const dimensionOptions: {
    id: ViewDimension;
    label: string;
    icon: React.ElementType;
  }[] = [{
    id: 'global',
    label: 'Vista Global',
    icon: Globe
  }, {
    id: 'agent',
    label: 'Por Agente',
    icon: Bot
  }, {
    id: 'service',
    label: 'Por Servicio MCP',
    icon: Layers
  }, {
    id: 'chat',
    label: 'Por Conversación',
    icon: MessageSquare
  }];
  const agentOptions = agents.map(a => ({
    id: a.id,
    label: a.name,
    icon: a.icon,
    color: a.color,
    sublabel: a.hired ? `${a.msgs} msgs · $${a.price}/msg` : 'No contratado'
  }));
  const serviceOptions = services.map(s => ({
    id: s.id,
    label: s.name,
    icon: s.icon,
    color: s.color,
    sublabel: `${s.msgs} llamadas este mes`
  }));
  const chatOptions = chatSessions.map(c => ({
    id: c.id,
    label: c.title,
    sublabel: `${c.msgs} msgs · ${c.date}`
  }));
  // Chart rendering based on dimension
  const renderChart = () => {
    if (viewDimension === 'global') {
      return <ResponsiveContainer width="100%" height="100%" data-id="element-3513">
          <AreaChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 0
        }} data-id="element-3514">
            <defs data-id="element-3515">
              <linearGradient id="gMsgs" x1="0" y1="0" x2="0" y2="1" data-id="element-3516">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} data-id="element-3517" />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} data-id="element-3518" />
              </linearGradient>
              <linearGradient id="gErrors" x1="0" y1="0" x2="0" y2="1" data-id="element-3519">
                <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-3520" />
                <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-3521" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-3522" />

            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3523" />

            <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3524" />

            <Tooltip contentStyle={{
            backgroundColor: '#0f172a',
            borderColor: '#1e293b',
            borderRadius: '8px',
            fontSize: '12px'
          }} itemStyle={{
            color: '#e2e8f0'
          }} data-id="element-3525" />

            <Area type="monotone" dataKey="mensajes" name="Mensajes" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#gMsgs)" data-id="element-3526" />

            <Area type="monotone" dataKey="errores" name="Errores" stroke="#f43f5e" strokeWidth={2} fillOpacity={1} fill="url(#gErrors)" data-id="element-3527" />
          </AreaChart>
        </ResponsiveContainer>;
    }
    if (viewDimension === 'agent') {
      const agent = agents.find(a => a.id === selectedAgent);
      return <ResponsiveContainer width="100%" height="100%" data-id="element-3528">
          <AreaChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 0
        }} data-id="element-3529">
            <defs data-id="element-3530">
              <linearGradient id="gAgent" x1="0" y1="0" x2="0" y2="1" data-id="element-3531">
                <stop offset="5%" stopColor={agent?.chartColor || '#8b5cf6'} stopOpacity={0.3} data-id="element-3532" />

                <stop offset="95%" stopColor={agent?.chartColor || '#8b5cf6'} stopOpacity={0} data-id="element-3533" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-3534" />

            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3535" />

            <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3536" />

            <Tooltip contentStyle={{
            backgroundColor: '#0f172a',
            borderColor: '#1e293b',
            borderRadius: '8px',
            fontSize: '12px'
          }} itemStyle={{
            color: '#e2e8f0'
          }} data-id="element-3537" />

            <Area type="monotone" dataKey="mensajes" name="Mensajes" stroke={agent?.chartColor || '#8b5cf6'} strokeWidth={2} fillOpacity={1} fill="url(#gAgent)" data-id="element-3538" />

            <Area type="monotone" dataKey="tools" name="Tool Calls" stroke="#64748b" strokeWidth={1.5} fillOpacity={0} strokeDasharray="4 4" data-id="element-3539" />
          </AreaChart>
        </ResponsiveContainer>;
    }
    if (viewDimension === 'service') {
      const svc = services.find(s => s.id === selectedService);
      return <ResponsiveContainer width="100%" height="100%" data-id="element-3540">
          <BarChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 0
        }} data-id="element-3541">
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-3542" />

            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3543" />

            <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3544" />

            <Tooltip contentStyle={{
            backgroundColor: '#0f172a',
            borderColor: '#1e293b',
            borderRadius: '8px',
            fontSize: '12px'
          }} itemStyle={{
            color: '#e2e8f0'
          }} data-id="element-3545" />

            <Bar dataKey="lecturas" name="Lecturas (GET)" fill={svc?.chartColor || '#60a5fa'} radius={[4, 4, 0, 0]} opacity={0.8} data-id="element-3546" />

            <Bar dataKey="escrituras" name="Escrituras (POST/PUT)" fill="#a78bfa" radius={[4, 4, 0, 0]} opacity={0.6} data-id="element-3547" />

            <Bar dataKey="errores" name="Errores" fill="#f43f5e" radius={[4, 4, 0, 0]} opacity={0.5} data-id="element-3548" />
          </BarChart>
        </ResponsiveContainer>;
    }
    if (viewDimension === 'chat') {
      return <ResponsiveContainer width="100%" height="100%" data-id="element-3549">
          <AreaChart data={chartData} margin={{
          top: 10,
          right: 10,
          left: -20,
          bottom: 0
        }} data-id="element-3550">
            <defs data-id="element-3551">
              <linearGradient id="gChat" x1="0" y1="0" x2="0" y2="1" data-id="element-3552">
                <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} data-id="element-3553" />
                <stop offset="95%" stopColor="#6366f1" stopOpacity={0} data-id="element-3554" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} data-id="element-3555" />

            <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3556" />

            <YAxis stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} data-id="element-3557" />

            <Tooltip contentStyle={{
            backgroundColor: '#0f172a',
            borderColor: '#1e293b',
            borderRadius: '8px',
            fontSize: '12px'
          }} itemStyle={{
            color: '#e2e8f0'
          }} data-id="element-3558" />

            <Area type="monotone" dataKey="mensajes" name="Mensajes" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#gChat)" data-id="element-3559" />

            <Area type="monotone" dataKey="tokens" name="Tokens (est.)" stroke="#64748b" strokeWidth={1} fillOpacity={0} strokeDasharray="4 4" data-id="element-3560" />
          </AreaChart>
        </ResponsiveContainer>;
    }
    return null;
  };
  // Context label for chart
  const getContextLabel = () => {
    if (viewDimension === 'agent') {
      const a = agents.find(ag => ag.id === selectedAgent);
      return a ? `${a.name} · $${a.price}/msg` : '';
    }
    if (viewDimension === 'service') {
      const s = services.find(sv => sv.id === selectedService);
      return s ? `${s.name} · ${s.msgs} llamadas` : '';
    }
    if (viewDimension === 'chat') {
      const c = chatSessions.find(ch => ch.id === selectedChat);
      return c ? `${c.title}` : '';
    }
    return 'Todos los agentes y servicios';
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200" data-id="element-3561">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-3562">
        {/* SECTION 1: PLAN BANNER */}
        <div className={`relative overflow-hidden rounded-2xl border p-6 lg:p-8 ${isExhausted ? 'bg-rose-950/20 border-rose-900/50' : 'bg-indigo-950/20 border-indigo-900/50'}`} data-id="element-3563">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-3564" />

          <div className="relative z-10 flex flex-col lg:flex-row gap-8 justify-between" data-id="element-3565">
            <div className="flex-1" data-id="element-3566">
              <div className="flex items-center gap-3 mb-4" data-id="element-3567">
                {isExhausted ? <>
                    <Badge variant="error" data-id="element-3568">Free Tier Agotado</Badge>
                    <Badge variant="warning" data-id="element-3569">Pay-per-use Activo</Badge>
                  </> : <>
                    <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30" data-id="element-3570">
                      Plan Actual: Free Tier
                    </Badge>
                    <span className="text-xs text-slate-400" data-id="element-3571">
                      Renueva el 1 de Noviembre
                    </span>
                  </>}
              </div>

              <h2 className="text-2xl lg:text-3xl font-bold text-slate-100 mb-2" data-id="element-3572">
                {isExhausted ? `Usaste los ${freeTierLimit} mensajes gratis` : `Tenés ${freeTierLimit} mensajes gratis por mes`}
              </h2>
              <p className="text-slate-400 mb-6 max-w-xl" data-id="element-3573">
                {isExhausted ? 'Cada mensaje se cobra según la tarifa del agente.' : 'Al superar el límite, cada mensaje se cobra según el agente.'}
              </p>

              <div className="max-w-md" data-id="element-3574">
                <div className="flex justify-between text-sm mb-2" data-id="element-3575">
                  <span className="font-medium text-slate-300" data-id="element-3576">
                    {usedMessages} / {freeTierLimit} msgs
                  </span>
                  <span className="text-slate-500" data-id="element-3577">
                    {Math.max(0, freeTierLimit - usedMessages)} restantes
                  </span>
                </div>
                <div className="h-2.5 bg-slate-900 rounded-full overflow-hidden border border-slate-800" data-id="element-3578">
                  <div className={`h-full rounded-full transition-all duration-1000 ${isExhausted ? 'bg-rose-500' : progressPercent > 90 ? 'bg-rose-500' : 'bg-gradient-to-r from-indigo-500 to-violet-500'}`} style={{
                  width: `${progressPercent}%`
                }} data-id="element-3579" />
                </div>
              </div>
            </div>

            <div className="lg:w-96 shrink-0 flex flex-col justify-center" data-id="element-3580">
              {isExhausted ? <div className="bg-slate-900/80 rounded-xl p-4 border border-slate-800" data-id="element-3581">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-3582">
                    Tarifas Pay-per-use
                  </h4>
                  <div className="grid grid-cols-2 gap-2" data-id="element-3583">
                    {agents.filter(a => a.hired).map(a => {
                  const Icon = a.icon;
                  return <div key={a.id} className="flex items-center justify-between text-xs p-1.5 rounded bg-slate-950 border border-slate-800/50" data-id="element-3584">
                            <div className="flex items-center gap-1.5 truncate" data-id="element-3585">
                              <Icon size={12} className={a.color} data-id="element-3586" />
                              <span className="truncate text-slate-300" data-id="element-3587">
                                {a.shortName}
                              </span>
                            </div>
                            <span className="font-mono text-slate-400" data-id="element-3588">
                              ${a.price}
                            </span>
                          </div>;
                })}
                  </div>
                </div> : <div className="bg-gradient-to-br from-slate-900 to-slate-900/50 rounded-xl p-6 border border-slate-800 text-center" data-id="element-3589">
                  <h3 className="text-lg font-bold text-slate-100 mb-2" data-id="element-3590">
                    Upgrade a Pro
                  </h3>
                  <p className="text-sm text-slate-400 mb-4" data-id="element-3591">
                    Mensajes ilimitados, todos los agentes y soporte
                    prioritario.
                  </p>
                  <Button className="w-full bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 border-0" data-id="element-3592">
                    Upgrade por $49/mes
                  </Button>
                </div>}
            </div>
          </div>
        </div>

        {/* SECTION 2: SUMMARY CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3593">
          {[{
          label: 'Gasto del Mes',
          value: `$${totalCost.toFixed(2)}`,
          sub: '+12% vs mes anterior',
          subColor: 'text-emerald-400',
          icon: CreditCard,
          iconBg: 'bg-emerald-950/30 text-emerald-400',
          subIcon: TrendingUp
        }, {
          label: 'Mensajes Totales',
          value: usedMessages.toString(),
          sub: `De ${freeTierLimit} incluidos`,
          subColor: 'text-slate-500',
          icon: MessageSquare,
          iconBg: 'bg-indigo-950/30 text-indigo-400'
        }, {
          label: 'Agentes Activos',
          value: agents.filter(a => a.hired).length.toString(),
          sub: 'De 8 disponibles',
          subColor: 'text-slate-500',
          icon: Bot,
          iconBg: 'bg-violet-950/30 text-violet-400'
        }, {
          label: 'Llamadas MCP',
          value: services.reduce((s, sv) => s + sv.msgs, 0).toString(),
          sub: '4 servicios conectados',
          subColor: 'text-slate-500',
          icon: Layers,
          iconBg: 'bg-sky-950/30 text-sky-400'
        }].map(card => {
          const CardIcon = card.icon;
          const SubIcon = card.subIcon;
          return <Card key={card.label} className="bg-slate-900 border-slate-800" data-id="element-3594">
                <CardContent className="p-5" data-id="element-3595">
                  <div className="flex justify-between items-start mb-2" data-id="element-3596">
                    <p className="text-sm font-medium text-slate-400" data-id="element-3597">
                      {card.label}
                    </p>
                    <div className={`p-2 rounded-lg ${card.iconBg}`} data-id="element-3598">
                      <CardIcon size={16} data-id="element-3599" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-100" data-id="element-3600">
                    {card.value}
                  </h3>
                  <p className={`text-xs flex items-center gap-1 mt-1 ${card.subColor}`} data-id="element-3601">
                    {SubIcon && <SubIcon size={12} data-id="element-3602" />} {card.sub}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* SECTION 3: USAGE CHART WITH ADVANCED FILTERS */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3603">
          <Card className="lg:col-span-2 bg-slate-900 border-slate-800 flex flex-col" data-id="element-3604">
            <CardHeader className="pb-2" data-id="element-3605">
              <div className="flex flex-col gap-4" data-id="element-3606">
                <div className="flex items-center justify-between" data-id="element-3607">
                  <CardTitle className="flex items-center gap-2" data-id="element-3608">
                    <Filter size={16} className="text-slate-500" data-id="element-3609" /> Uso de
                    Mensajes
                  </CardTitle>

                  {/* Time Range Selector */}
                  <div className="flex items-center bg-slate-800 rounded-lg p-0.5 border border-slate-700" data-id="element-3610">
                    {(['7d', '14d', '30d', '90d'] as TimeRange[]).map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${timeRange === range ? 'bg-slate-700 text-slate-200 shadow-sm' : 'text-slate-500 hover:text-slate-400'}`} data-id="element-3611">
                          {range}
                        </button>)}
                  </div>
                </div>

                {/* Dimension Filter Bar */}
                <div className="flex flex-wrap items-center gap-2" data-id="element-3612">
                  {/* Dimension Tabs */}
                  <div className="flex items-center bg-slate-950 rounded-lg p-0.5 border border-slate-800" data-id="element-3613">
                    {dimensionOptions.map(dim => {
                    const DimIcon = dim.icon;
                    return <button key={dim.id} onClick={() => setViewDimension(dim.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewDimension === dim.id ? 'bg-indigo-900/40 text-indigo-300 border border-indigo-500/40' : 'text-slate-500 hover:text-slate-300 border border-transparent'}`} data-id="element-3614">
                          <DimIcon size={12} data-id="element-3615" />
                          <span className="hidden sm:inline" data-id="element-3616">{dim.label}</span>
                        </button>;
                  })}
                  </div>

                  {/* Secondary Filter (depends on dimension) */}
                  {viewDimension === 'agent' && <Dropdown label="Agente" value={selectedAgent} options={agentOptions} onChange={setSelectedAgent} icon={Bot} data-id="element-3617" />}
                  {viewDimension === 'service' && <Dropdown label="Servicio" value={selectedService} options={serviceOptions} onChange={setSelectedService} icon={Layers} data-id="element-3618" />}
                  {viewDimension === 'chat' && <Dropdown label="Conversación" value={selectedChat} options={chatOptions} onChange={setSelectedChat} icon={MessageSquare} data-id="element-3619" />}
                </div>

                {/* Context Label */}
                <p className="text-xs text-slate-500 -mt-1 flex items-center gap-1.5" data-id="element-3620">
                  <Calendar size={10} data-id="element-3621" /> Últimos {days} días ·{' '}
                  {getContextLabel()}
                </p>
              </div>
            </CardHeader>
            <CardContent className="flex-1 min-h-[300px] pt-2" data-id="element-3622">
              {renderChart()}
            </CardContent>
          </Card>

          {/* SECTION 4: BREAKDOWN (changes with dimension) */}
          <Card className="bg-slate-900 border-slate-800 flex flex-col" data-id="element-3623">
            <CardHeader className="pb-3" data-id="element-3624">
              <CardTitle className="text-base" data-id="element-3625">
                {viewDimension === 'service' ? 'Desglose por Servicio' : viewDimension === 'chat' ? 'Conversaciones Recientes' : 'Desglose por Agente'}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-0" data-id="element-3626">
              <div className="flex-1 overflow-y-auto px-4 space-y-1" data-id="element-3627">
                {/* Agent Breakdown (default + agent view) */}
                {(viewDimension === 'global' || viewDimension === 'agent') && agents.map(agent => {
                const Icon = agent.icon;
                const cost = agent.msgs * agent.price;
                const maxMsgs = Math.max(...agents.map(a => a.msgs)) || 1;
                const barW = `${Math.max(agent.msgs / maxMsgs * 100, 2)}%`;
                const isHighlighted = viewDimension === 'agent' && agent.id === selectedAgent;
                return <button key={agent.id} onClick={() => {
                  setViewDimension('agent');
                  setSelectedAgent(agent.id);
                }} className={`w-full p-3 rounded-lg flex items-center justify-between text-left transition-all ${isHighlighted ? 'bg-indigo-950/30 ring-1 ring-indigo-500/30' : agent.hired ? 'bg-slate-950/50 hover:bg-slate-800/30' : 'opacity-40 grayscale'}`} data-id="element-3628">
                        <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-3629">
                          <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0" data-id="element-3630">
                            <Icon size={14} className={agent.color} data-id="element-3631" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-3632">
                            <p className="text-sm font-medium text-slate-200 truncate" data-id="element-3633">
                              {agent.name}
                            </p>
                            {agent.hired ? <div className="flex items-center gap-2 mt-1" data-id="element-3634">
                                <div className="h-1.5 flex-1 bg-slate-800 rounded-full overflow-hidden" data-id="element-3635">
                                  <div className={`h-full rounded-full ${agent.bg}`} style={{
                            width: barW
                          }} data-id="element-3636" />
                                </div>
                                <span className="text-[10px] text-slate-500 font-mono w-8" data-id="element-3637">
                                  {agent.msgs}
                                </span>
                              </div> : <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-3638">
                                No contratado
                              </p>}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-4" data-id="element-3639">
                          <p className="text-sm font-mono text-slate-300" data-id="element-3640">
                            ${cost.toFixed(2)}
                          </p>
                          <p className="text-[10px] text-slate-500" data-id="element-3641">
                            ${agent.price}/msg
                          </p>
                        </div>
                      </button>;
              })}

                {/* Service Breakdown */}
                {viewDimension === 'service' && services.map(svc => {
                const Icon = svc.icon;
                const maxMsgs = Math.max(...services.map(s => s.msgs)) || 1;
                const barW = `${Math.max(svc.msgs / maxMsgs * 100, 2)}%`;
                const isHighlighted = svc.id === selectedService;
                return <button key={svc.id} onClick={() => setSelectedService(svc.id)} className={`w-full p-3 rounded-lg flex items-center justify-between text-left transition-all ${isHighlighted ? 'bg-indigo-950/30 ring-1 ring-indigo-500/30' : 'bg-slate-950/50 hover:bg-slate-800/30'}`} data-id="element-3642">
                        <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-3643">
                          <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0" data-id="element-3644">
                            <Icon size={14} className={svc.color} data-id="element-3645" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-3646">
                            <p className="text-sm font-medium text-slate-200" data-id="element-3647">
                              {svc.name}
                            </p>
                            <div className="flex items-center gap-2 mt-1" data-id="element-3648">
                              <div className="h-1.5 flex-1 bg-slate-800 rounded-full overflow-hidden" data-id="element-3649">
                                <div className="h-full rounded-full" style={{
                            width: barW,
                            backgroundColor: svc.chartColor
                          }} data-id="element-3650" />
                              </div>
                              <span className="text-[10px] text-slate-500 font-mono w-8" data-id="element-3651">
                                {svc.msgs}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-4" data-id="element-3652">
                          <p className="text-sm font-mono text-slate-300" data-id="element-3653">
                            {svc.msgs}
                          </p>
                          <p className="text-[10px] text-slate-500" data-id="element-3654">llamadas</p>
                        </div>
                      </button>;
              })}

                {/* Chat Breakdown */}
                {viewDimension === 'chat' && chatSessions.map(chat => {
                const isHighlighted = chat.id === selectedChat;
                const agentDef = agents.find(a => a.id === chat.agent);
                const AgentIcon = agentDef?.icon || MessageSquare;
                return <button key={chat.id} onClick={() => setSelectedChat(chat.id)} className={`w-full p-3 rounded-lg flex items-center justify-between text-left transition-all ${isHighlighted ? 'bg-indigo-950/30 ring-1 ring-indigo-500/30' : 'bg-slate-950/50 hover:bg-slate-800/30'}`} data-id="element-3655">
                        <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-3656">
                          <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0" data-id="element-3657">
                            <AgentIcon size={14} className={agentDef?.color || 'text-slate-400'} data-id="element-3658" />
                          </div>
                          <div className="min-w-0 flex-1" data-id="element-3659">
                            <p className="text-sm font-medium text-slate-200 truncate" data-id="element-3660">
                              {chat.title}
                            </p>
                            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-3661">
                              {chat.date} · {agentDef?.shortName || 'Agent'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-4" data-id="element-3662">
                          <p className="text-sm font-mono text-slate-300" data-id="element-3663">
                            {chat.msgs}
                          </p>
                          <p className="text-[10px] text-slate-500" data-id="element-3664">msgs</p>
                        </div>
                      </button>;
              })}
              </div>

              <div className="p-4 border-t border-slate-800 bg-slate-950/50 mt-2" data-id="element-3665">
                <div className="flex justify-between items-center" data-id="element-3666">
                  <span className="text-sm font-medium text-slate-400" data-id="element-3667">
                    {viewDimension === 'service' ? 'Total Llamadas' : viewDimension === 'chat' ? 'Total Mensajes' : 'Total Estimado'}
                  </span>
                  <span className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-violet-400" data-id="element-3668">
                    {viewDimension === 'service' ? services.reduce((s, sv) => s + sv.msgs, 0) : viewDimension === 'chat' ? chatSessions.reduce((s, c) => s + c.msgs, 0) : `$${totalCost.toFixed(2)}`}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SECTION 5 & 6: INVOICES + SIDEBAR */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3669">
          <Card className="lg:col-span-2 bg-slate-900 border-slate-800" data-id="element-3670">
            <CardHeader data-id="element-3671">
              <CardTitle data-id="element-3672">Historial de Facturas</CardTitle>
            </CardHeader>
            <CardContent className="p-0" data-id="element-3673">
              <div className="overflow-x-auto" data-id="element-3674">
                <table className="w-full text-sm text-left" data-id="element-3675">
                  <thead className="text-xs text-slate-500 bg-slate-950/50 border-y border-slate-800" data-id="element-3676">
                    <tr data-id="element-3677">
                      <th className="px-6 py-3 font-medium" data-id="element-3678">Período</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3679">Mensajes</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3680">Agentes</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3681">Monto</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3682">Estado</th>
                      <th className="px-6 py-3 font-medium text-right" data-id="element-3683">
                        Acción
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/50" data-id="element-3684">
                    {invoices.map(inv => <tr key={inv.id} className="hover:bg-slate-800/20 transition-colors" data-id="element-3685">
                        <td className="px-6 py-4 font-medium text-slate-300" data-id="element-3686">
                          {inv.period}
                        </td>
                        <td className="px-6 py-4 text-slate-400 font-mono" data-id="element-3687">
                          {inv.msgs}
                        </td>
                        <td className="px-6 py-4 text-slate-400" data-id="element-3688">
                          {inv.agents}
                        </td>
                        <td className="px-6 py-4 font-mono text-slate-300" data-id="element-3689">
                          ${inv.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4" data-id="element-3690">
                          <Badge variant="success" data-id="element-3691">{inv.status}</Badge>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3692">
                          <button className="text-slate-500 hover:text-indigo-400 transition-colors p-1" data-id="element-3693">
                            <Download size={16} data-id="element-3694" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6" data-id="element-3695">
            <Card className="bg-slate-900 border-slate-800" data-id="element-3696">
              <CardHeader className="pb-3" data-id="element-3697">
                <CardTitle className="text-base" data-id="element-3698">Método de Pago</CardTitle>
              </CardHeader>
              <CardContent data-id="element-3699">
                <div className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-xl mb-4" data-id="element-3700">
                  <div className="flex items-center gap-3" data-id="element-3701">
                    <div className="w-10 h-6 bg-slate-800 rounded flex items-center justify-center border border-slate-700" data-id="element-3702">
                      <span className="text-[10px] font-bold text-white italic" data-id="element-3703">
                        VISA
                      </span>
                    </div>
                    <div data-id="element-3704">
                      <p className="text-sm font-medium text-slate-200" data-id="element-3705">
                        •••• 4242
                      </p>
                      <p className="text-[10px] text-slate-500" data-id="element-3706">Expira 12/28</p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300" data-id="element-3707">
                  Cambiar Método
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800" data-id="element-3708">
              <CardHeader className="pb-3" data-id="element-3709">
                <CardTitle className="text-base flex items-center gap-2" data-id="element-3710">
                  <AlertTriangle size={16} className="text-amber-400" data-id="element-3711" /> Alertas
                  de Gasto
                </CardTitle>
              </CardHeader>
              <CardContent data-id="element-3712">
                <div className="flex items-center justify-between mb-4" data-id="element-3713">
                  <div data-id="element-3714">
                    <p className="text-sm font-medium text-slate-200" data-id="element-3715">
                      Límite Mensual
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-3716">
                      Avisar al superar monto
                    </p>
                  </div>
                  <button onClick={() => setLimitAlert(!limitAlert)} className={`relative w-10 h-5 rounded-full transition-colors ${limitAlert ? 'bg-emerald-600' : 'bg-slate-700'}`} data-id="element-3717">
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${limitAlert ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-3718" />
                  </button>
                </div>
                {limitAlert && <div className="relative" data-id="element-3719">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-3720">
                      $
                    </span>
                    <input type="number" value={limitAmount} onChange={e => setLimitAmount(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-7 pr-3 text-sm text-slate-200 focus:outline-none focus:border-indigo-500" data-id="element-3721" />
                  </div>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>;
}